import {TreatmentLine, TreatmentLinePrescriber} from "@/medication-checkup/interfaces/treatment-selections";
import FileUrl from "@/core/interfaces/FileUrl";

/**
 * A prescription the collection lays out as a sheet: the scanned document
 * behind the last dispensing of a prescriber, with the lines delivered on it.
 *
 * The HDS serves lines, not prescriptions: each prescriber of a line carries
 * the key of their most recent dispensing of the product. A sheet is one of
 * those keys, so it is a dispensing the LGO scanned — never the history of
 * the prescriptions of the patient, which nothing serves. A prescribed line
 * whose prescribers carry no key at all lands on a sheet of its own per
 * prescriber, with no document to show.
 */
export interface TreatmentSheet {
    key: string;
    /**
     * The key the HDS serves the document by — null on a sheet standing for
     * a prescriber whose prescription was not scanned.
     */
    prescriptionKey: string | null;
    prescriber: string | null;
    deliveredAt: string | null;
    lines: TreatmentLine[];
}

/**
 * The lines of the collection by where the pharmacist reviews them: on the
 * sheets of the prescriptions, among the counter purchases, or among the
 * products added during the interview.
 */
export interface TreatmentSources {
    sheets: TreatmentSheet[];
    counter: TreatmentLine[];
    added: TreatmentLine[];
}

const byLatest = (first: TreatmentSheet, second: TreatmentSheet): number => {
    if (first.deliveredAt === second.deliveredAt) {
        return 0;
    }

    if (first.deliveredAt === null) {
        return 1;
    }

    if (second.deliveredAt === null) {
        return -1;
    }

    return second.deliveredAt.localeCompare(first.deliveredAt);
};

/**
 * A prescriber whose last dispensing the LGO scanned a document for.
 */
type ScannedPrescriber = TreatmentLinePrescriber & {prescription_key: string};

const isScanned = (prescriber: TreatmentLinePrescriber): prescriber is ScannedPrescriber => {
    return prescriber.prescription_key !== null;
};

/**
 * The sheet of a scanned prescriber's last dispensing.
 */
const sheetOf = (prescriber: ScannedPrescriber, lines: TreatmentLine[]): TreatmentSheet => ({
    key: prescriber.prescription_key,
    prescriptionKey: prescriber.prescription_key,
    prescriber: prescriber.name,
    deliveredAt: prescriber.delivered_at,
    lines
});

const TreatmentSheets = () => {
    return {
        /**
         * Sort the lines of the collection out, the most recent prescription
         * first. A line delivered by several prescribers shows on each of
         * their sheets: it stays one line, checked and dosed once.
         */
        sources(lines: TreatmentLine[]): TreatmentSources {
            const sheets = new Map<string, TreatmentSheet>();

            const place = (sheet: TreatmentSheet, line: TreatmentLine) => {
                const existing = sheets.get(sheet.key) ?? sheet;

                if (!existing.lines.includes(line)) {
                    existing.lines.push(line);
                }

                if (sheet.deliveredAt !== null && (existing.deliveredAt === null || sheet.deliveredAt > existing.deliveredAt)) {
                    existing.deliveredAt = sheet.deliveredAt;
                }

                sheets.set(sheet.key, existing);
            };

            lines
                .filter(line => line.collected && line.source === 'prescription')
                .forEach(line => {
                    const prescribers = line.history?.prescriptors ?? [];
                    const scanned = prescribers.filter(isScanned);

                    scanned.forEach(prescriber => place(sheetOf(prescriber, []), line));

                    if (scanned.length > 0) {
                        return;
                    }

                    if (prescribers.length === 0) {
                        place({
                            key: 'prescriber:unknown',
                            prescriptionKey: null,
                            prescriber: null,
                            deliveredAt: null,
                            lines: []
                        }, line);

                        return;
                    }

                    prescribers.forEach(prescriber => place({
                        key: `prescriber:${prescriber.code}`,
                        prescriptionKey: null,
                        prescriber: prescriber.name,
                        deliveredAt: prescriber.delivered_at,
                        lines: []
                    }, line));
                });

            return {
                sheets: [...sheets.values()].sort(byLatest),
                counter: lines.filter(line => line.collected && line.source === 'automedication'),
                added: lines.filter(line => !line.collected)
            };
        },

        /**
         * The scanned prescriptions behind a line, most recent first.
         */
        sheetsOf(line: TreatmentLine): TreatmentSheet[] {
            return (line.history?.prescriptors ?? [])
                .filter(isScanned)
                .map(prescriber => sheetOf(prescriber, [line]))
                .sort(byLatest);
        },

        /**
         * The file urls of a prescription, on the HDS route serving it by its
         * composite key. The pdf is absolute for the print window; the rest
         * is relative, fetched through the secured client.
         */
        urls(key: string): FileUrl & {pdf: string} {
            const route = `prescriptions/${key}/file`;

            return {
                pdf: `${import.meta.env.VITE_HDS_URL}/${route}`,
                raw: `${route}?format=raw`,
                thumbnail: {
                    large: `${route}?format=large`,
                    small: `${route}?format=small`
                }
            };
        }
    };
};

export default TreatmentSheets();
